import { UnlockPadlock } from '@/components/motion/UnlockPadlock';
import { useUnlockSettle } from '@/components/motion/use-unlock-settle';
import { useQuotaUpgrade } from '@/components/subscription/plan-usage/use-quota-upgrade';
import {
  useUnlockCreatorProfileAction,
  type ProfileUnlockTarget,
} from '@/hooks/actions/use-unlock-creator-profile-action';
import { PlanLimit, type CreatorProfileUnlockOutcome, type PlanQuotaMeta } from '@fluenceur/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContactPrimaryButton from '../ContactPrimaryButton';

interface ContactUnlockButtonProps {
  target: ProfileUnlockTarget;
  /** The month's unlocks, on a plan that counts them (`limit` not null). */
  quota: PlanQuotaMeta & { limit: number };
  onUnlocked: (outcome: CreatorProfileUnlockOutcome) => void;
}

/**
 * The drawer's primary button on a masked prospect while the free plan counts
 * profile unlocks. The cost is in the label, so there is no confirmation: one
 * click spends one unlock, and the button stays disabled while it runs. With
 * no unlock left it opens the Essential plan choice instead. Once unlocked,
 * the padlock opens and the button reads "Profile unlocked" for a moment
 * before the drawer settles back on the email button.
 */
const ContactUnlockButton = ({ target, quota, onUnlocked }: ContactUnlockButtonProps) => {
  const { t } = useTranslation();
  const unlock = useUnlockCreatorProfileAction();
  const upgrade = useQuotaUpgrade(PlanLimit.PROFILE_UNLOCKS, quota);
  const [opened, setOpened] = useState(false);
  const settle = useUnlockSettle(onUnlocked);
  const remaining = Math.max(quota.limit - quota.used, 0);

  const label = opened
    ? t('planLocks.unlock.done')
    : remaining === 0
      ? t('planLocks.unlock.buttonNone')
      : remaining === 1
        ? t('planLocks.unlock.buttonLast')
        : t('planLocks.unlock.button', { count: remaining });

  const handleClick = () => {
    if (remaining === 0) {
      upgrade();
      return;
    }
    unlock.mutate(target, {
      onSuccess: ({ outcome }) => {
        setOpened(true);
        settle(outcome);
      },
    });
  };

  return (
    <ContactPrimaryButton
      flex="1"
      h="38px"
      loading={unlock.isPending}
      disabled={unlock.isPending}
      // Open padlock playing: not clickable, without the greyed disabled look.
      pointerEvents={opened ? 'none' : undefined}
      aria-disabled={opened || undefined}
      onClick={handleClick}
    >
      <UnlockPadlock open={opened} />
      {label}
    </ContactPrimaryButton>
  );
};

export default ContactUnlockButton;
